import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react'

import { useParams } from 'react-router-dom'

import { toast } from 'react-toastify'

import {
  addDoc,
  collection,
  query,
  onSnapshot,
  orderBy,
  doc,
  updateDoc,
} from 'firebase/firestore'

import { db } from '../../../../../infra/firebase/firebase-app'

import Chat from '../../../../../presentation/pages/common/chat'

import { useMe } from '../../../../../presentation/contexts/me'

import { makeDoctors } from '../../../../usecases/doctors/remote-doctors-factory'
import { makeContractors } from '../../../../usecases/contractors/remote-contractors-factory'
import { makeRequests } from '../../../../usecases/requests/remote-requests-factory'

import { debounce } from './utils/debouce'

const MakeChat = () => {
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const [messages, setMessages] = useState<any>([])

  const [interlocutor, setInterlocutor] = useState<any>({})

  const { interlocutorId, interlocutorType } = useParams<{
    interlocutorId: string
    interlocutorType: string
  }>()

  const { me } = useMe()

  const scrollToBottom = useCallback(
    debounce(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView()
      }
    }, 300),
    [messages],
  )

  useEffect(() => {
    scrollToBottom()
  }, [scrollToBottom])

  useEffect(() => {
    if (me?.id && interlocutorId && db) {
      const messagesCollection = collection(db, 'messages')
      const q = query(messagesCollection, orderBy('sendingDate', 'desc'))

      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          const messagesFetched: any = querySnapshot.docs
            .map((docFetched) => ({
              id: docFetched.id,
              ...docFetched.data(),
            }))
            .filter((message: any) => {
              const isSender = message.senderId === String(me?.id)
              const isReceiver = message.receiverId === String(me?.id)
              const isInterlocutorSender = message.senderId === String(interlocutorId)
              const isInterlocutorReceiver = message.receiverId === String(interlocutorId)

              return (
                (isSender && isInterlocutorReceiver)
                || (isReceiver && isInterlocutorSender)
              )
            })

          const messagesToRender = messagesFetched.map((message: any) => ({
            ...message,
            isReceived: message.senderId !== String(me?.id),
          }))

          setMessages(messagesToRender)
        },
        (error) => {
          toast.error(`Erro ao carregar mensagens: ${error.message}`)
        },
      )

      return () => unsubscribe()
    }

    return undefined
  }, [me?.id, interlocutorId])

  useEffect(() => {
    const readReceivedMessages = () => {
      const receveidMessages = messages.filter(
        (message: any) => message.senderId === interlocutorId,
      )

      receveidMessages.forEach(async (message: any) => {
        if (!db) return

        const messageDocRef = doc(db, 'messages', message.id)

        await updateDoc(messageDocRef, {
          isRead: true,
        })
      })
    }

    readReceivedMessages()
  }, [messages])

  useEffect(() => {
    const fetchInterlocutor = async () => {
      try {
        let fetchedInterlocutor

        if (interlocutorId && interlocutorType) {
          switch (interlocutorType) {
            case 'MEDICO':
              fetchedInterlocutor = await makeDoctors().loadById(
                Number(interlocutorId),
              )
              break
            case 'CONTRATANTE':
              fetchedInterlocutor = await makeContractors().loadById(
                Number(interlocutorId),
              )
              break
            default:
              break
          }
        }

        if (fetchedInterlocutor) {
          setInterlocutor(fetchedInterlocutor)
        }
      } catch (error: any) {
        toast.error(error.message)
      }
    }

    fetchInterlocutor()
  }, [interlocutorId, interlocutorType])

  useEffect(() => {
    document.title = `BuscarMédicos - Chat com ${
      interlocutor?.corporateName || interlocutor?.firstName
    }`
  }, [interlocutor])

  const handleSend = async (message: string) => {
    try {
      if (!db) return

      const messageToSend = {
        message,
        senderId: String(me?.id),
        receiverId: interlocutorId,
        sendingDate: new Date().toISOString(),
        isRead: false,
      }

      const sendedMessage = await addDoc(
        collection(db, 'messages'),
        messageToSend,
      )

      if (!messages.length) {
        switch (interlocutorType) {
          case 'MEDICO':
            await makeRequests('/solicitations').post({
              contractorId: me?.id,
              doctorId: interlocutorId,
            })
            break
          case 'CONTRATANTE':
            await makeRequests('/solicitations').post({
              contractorId: interlocutorId,
              doctorId: me?.id,
            })
            break
          default:
            break
        }
      }

      setMessages([
        {
          ...messageToSend,
          id: sendedMessage.id,
          isReceived: false,
        },
        ...messages,
      ])
    } catch (error: any) {
      toast.error(`Erro ao enviar mensagem: ${error.message}`)
    }
  }

  const interlocutorName = useMemo(
    () => (interlocutorType === 'MEDICO'
      ? interlocutor?.firstName
      : interlocutor?.corporateName),
    [interlocutor, interlocutorType],
  )

  const chatProps = useMemo(
    () => ({
      messages,
      handleSend,
      ref: messagesEndRef,
      interlocutor: interlocutorName,
    }),
    [messages, handleSend, messagesEndRef, interlocutorName],
  )

  return <Chat {...chatProps} />
}

export default MakeChat
